import { useApolloClient } from '@vue/apollo-composable';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { MyViewPreferencesDocument } from '@dott/graphql';
import type { ViewPreferenceFieldsFragment } from '@dott/graphql';

import { VIEW_PREFERENCES_VERSION, parseViewPreferences } from '@/shared/view-preferences/schema';
import type { ViewPreferenceContext, ViewPreferencesBlob } from '@/shared/view-preferences/schema';

/**
 * The caller's view-preference rows plus the shared workspace defaults —
 * loaded once at bootstrap (kicked fire-and-forget from the auth middleware)
 * so list pages can resolve their remembered layout before mounting a view.
 */
export const useViewPreferencesStore = defineStore('viewPreferences', () => {
  const { client } = useApolloClient();

  const records = ref<ViewPreferenceFieldsFragment[]>([]);

  // Which user the records belong to: a same-tab re-login as another user
  // must reload instead of serving the previous user's rows.
  let loadedForUserId: string | null = null;
  let readyPromise: Promise<void> | null = null;

  const sameScope = (
    record: ViewPreferenceFieldsFragment,
    context: string,
    contextId: string | null,
  ): boolean => record.context === context && (record.contextId ?? null) === contextId;

  const findRecord = (
    context: ViewPreferenceContext,
    contextId: string | null,
    isDefault: boolean,
  ): ViewPreferenceFieldsFragment | undefined =>
    records.value.find(
      (record) => sameScope(record, context, contextId) && record.isDefault === isDefault,
    );

  const load = (userId: string): Promise<void> => {
    if (readyPromise && loadedForUserId === userId) {
      return readyPromise;
    }

    loadedForUserId = userId;
    // Drop the previous user's rows immediately — a same-tab re-login must not
    // leave them readable while the new user's query is in flight.
    records.value = [];
    readyPromise = (async () => {
      try {
        const result = await client.query({
          query: MyViewPreferencesDocument,
          fetchPolicy: 'network-only',
        });

        records.value = result.data?.myViewPreferences ?? [];
      } catch (error) {
        // Preferences are display sugar: a failed bootstrap load silently
        // falls back to code defaults.
        console.error('[stores/viewPreferences] Failed to load view preferences:', error);
        records.value = [];
      }
    })();

    return readyPromise;
  };

  /**
   * Re-run the query, bypassing the one-per-user memo. Needed whenever a saved
   * view is created or first becomes visible: the switcher discovers it, but
   * its shared preference row is not in this store, so the view would open on
   * code defaults — discarding the configuration it was saved with.
   */
  const refresh = async (): Promise<void> => {
    if (loadedForUserId === null) {
      return;
    }

    try {
      const result = await client.query({
        query: MyViewPreferencesDocument,
        fetchPolicy: 'network-only',
      });

      records.value = result.data?.myViewPreferences ?? [];
    } catch (error) {
      console.error('[stores/viewPreferences] Failed to refresh view preferences:', error);
    }
  };

  /**
   * Resolves when the bootstrap load has settled — success OR failure. If a
   * load was never kicked (anonymous visit), it resolves immediately so pages
   * render code defaults instead of hanging on the gate.
   */
  const whenReady = (): Promise<void> => readyPromise ?? Promise.resolve();

  /**
   * The blob a surface should use, merged by top-level section: my section ??
   * shared section (code defaults are applied by the consumer). Future
   * sections (display options) resolve independently through the spreads.
   * Invalid/corrupt rows read as absent.
   */
  const getEffective = (
    context: ViewPreferenceContext,
    contextId: string | null,
  ): ViewPreferencesBlob => {
    const shared = parseViewPreferences(findRecord(context, contextId, true)?.preferences);
    const mine = parseViewPreferences(findRecord(context, contextId, false)?.preferences);

    const effective: ViewPreferencesBlob = { ...shared, ...mine, v: VIEW_PREFERENCES_VERSION };

    // `views` holds one sub-section per view (table, board), and each resolves
    // on its own: a user who only re-ordered their board must keep reading the
    // shared TABLE settings, not the code defaults. For rows carrying only
    // `table` this is exactly the section-level merge above.
    if (shared?.views || mine?.views) {
      effective.views = { ...shared?.views, ...mine?.views };
    }

    return effective;
  };

  /** Whether the caller has a personal row for a surface (Reset visibility). */
  const hasMine = (context: ViewPreferenceContext, contextId: string | null): boolean =>
    Boolean(findRecord(context, contextId, false));

  /**
   * The caller's own parsed blob for a surface (no shared/default layering) —
   * what a full-blob personal write composes on top of. Invalid rows read as
   * absent.
   */
  const getMine = (
    context: ViewPreferenceContext,
    contextId: string | null,
  ): ViewPreferencesBlob | null =>
    parseViewPreferences(findRecord(context, contextId, false)?.preferences);

  /** Optimistically upsert a record — matched by scope + layer, not by id. */
  const applyLocal = (record: ViewPreferenceFieldsFragment): void => {
    const index = records.value.findIndex(
      (candidate) =>
        sameScope(candidate, record.context, record.contextId ?? null)
        && candidate.isDefault === record.isDefault,
    );

    records.value
      = index === -1
        ? [...records.value, record]
        : records.value.map((candidate, i) => (i === index ? record : candidate));
  };

  /** Optimistically drop the caller's row for a surface (reset-to-default). */
  const dropMine = (context: ViewPreferenceContext, contextId: string | null): void => {
    records.value = records.value.filter(
      (record) => !(sameScope(record, context, contextId) && !record.isDefault),
    );
  };

  return {
    records,
    load,
    refresh,
    whenReady,
    getEffective,
    hasMine,
    getMine,
    applyLocal,
    dropMine,
  };
});
